"use client"

import { Fragment, useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { Ellipsis } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { AppControls } from "@/components/layout/AppControls"
import { ToolbarDivider } from "@/components/layout/ToolbarDivider"
import { PHONE_QUERY, useMediaQuery } from "@/hooks/use-media-query"
import { cn } from "@/lib/utils"

/** One group of controls: the buttons between two dividers. */
export interface ToolbarGroup {
  /** Stable identity, for measuring and for React. */
  id: string
  /**
   * How long the group holds its place as the toolbar narrows. The lowest collapses first; groups
   * of equal priority collapse from the left.
   */
  priority: number
  content: ReactNode
}

/**
 * Marks a control that opens something, or moves the page — the rail, find, go-to, go to top. Pressed
 * inside the ⋯ popover, it closes the popover, which would otherwise sit over what it just opened.
 * Controls a reader presses again and again, such as the text size, leave it open.
 */
export const DISMISS_OVERFLOW_ATTRIBUTE = "data-dismiss-overflow"

/** The gap either side of a divider (`gap-6`), and the divider itself. */
const SEPARATOR_PX = 24 + 1 + 24
/** The overflow button (a standard 32px icon button) and the separator after it, when groups remain. */
const MORE_PX = 32 + SEPARATOR_PX

/**
 * A row of control groups that folds into an overflow button rather than scrolling.
 *
 * When the groups do not fit, whole groups collapse — lowest priority first — into a ⋯ button at
 * the start of the row, which opens a popover holding them, one group per row. What remains on
 * the toolbar is always whole and clickable, and "there is more" is a button rather than an edge
 * that may or may not look cut off.
 *
 * The popover holds the SAME controls, not menu versions of them, so a toggle, a segmented
 * control or a button with a pending spinner works in the popover exactly as it does in the row.
 *
 * The ⋯ button goes at the start because the row is right aligned: appearing and disappearing at
 * the left edge moves nothing to its right, so the controls a reader uses most — at the right
 * end — stay where they are as the window is resized.
 *
 * ── Measuring ───────────────────────────────────────────────────────────────────────────
 *
 * The room is the toolbar's own width, which the header gives it and which does not depend on
 * what is inside it — so deciding what to show can never change the answer and loop. Each group's
 * width is measured while it is on the toolbar and remembered once it has collapsed, which is all
 * the decision to bring it back needs. Measured in a layout effect, so a toolbar that does not fit
 * is corrected before it is ever painted overflowing.
 *
 * ── On a phone ───────────────────────────────────────────────────────────────────────────
 *
 * Everything folds, and the app's own buttons — theme, language, history, feedback — fold in with
 * it, as the popover's last row: the header is too narrow for any of them beside the wordmark, and
 * one ⋯ beside the account button is the whole of it. The header leaves its own copy of those
 * buttons out while a toolbar is on the page, which it finds by `data-toolbar` (see AppHeader).
 */
export function Toolbar({
  groups,
  moreLabel,
  className,
}: {
  groups: ToolbarGroup[]
  /** The overflow button's tooltip and accessible name. */
  moreLabel: string
  className?: string
}) {
  const root = useRef<HTMLDivElement>(null)
  const nodes = useRef(new Map<string, HTMLElement>())
  const widths = useRef(new Map<string, number>())
  const [collapsed, setCollapsed] = useState<string[]>([])
  const phone = useMediaQuery(PHONE_QUERY)
  const [moreOpen, setMoreOpen] = useState(false)

  const decide = useCallback(() => {
    const container = root.current
    if (!container || phone) return

    // Remember the width of every group that is on the toolbar right now.
    for (const [id, node] of nodes.current) widths.current.set(id, node.offsetWidth)

    const available = container.clientWidth
    const widthOf = (group: ToolbarGroup) => widths.current.get(group.id) ?? 0
    const needed = (shown: ToolbarGroup[], anyCollapsed: boolean) =>
      shown.reduce((sum, group) => sum + widthOf(group), 0) +
      Math.max(0, shown.length - 1) * SEPARATOR_PX +
      (anyCollapsed ? MORE_PX : 0)

    // Lowest priority first; among equals, the leftmost first.
    const order = groups
      .map((group, index) => ({ group, index }))
      .sort((a, b) => a.group.priority - b.group.priority || a.index - b.index)
      .map(({ group }) => group)

    const hidden = new Set<string>()
    for (const group of order) {
      const shown = groups.filter((candidate) => !hidden.has(candidate.id))
      if (needed(shown, hidden.size > 0) <= available) break
      hidden.add(group.id)
    }

    const next = groups.filter((group) => hidden.has(group.id)).map((group) => group.id)
    setCollapsed((previous) => (previous.join("|") === next.join("|") ? previous : next))
  }, [groups, phone])

  useLayoutEffect(() => {
    // Measuring and correcting before paint is the point: a ResizeObserver's first callback arrives
    // after the browser has already drawn the toolbar overflowing.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    decide()
    const container = root.current
    if (!container) return
    const observer = new ResizeObserver(decide)
    observer.observe(container)
    for (const node of nodes.current.values()) observer.observe(node)
    return () => observer.disconnect()
  }, [decide, collapsed])

  const shown = phone ? [] : groups.filter((group) => !collapsed.includes(group.id))
  const hidden = phone ? groups : groups.filter((group) => collapsed.includes(group.id))

  return (
    <div
      ref={root}
      data-toolbar=""
      className={cn("flex w-full min-w-0 flex-row items-center justify-end gap-6", className)}
    >
      {(hidden.length > 0 || phone) && (
        <>
          <Popover open={moreOpen} onOpenChange={setMoreOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button variant="secondary" size="icon" aria-label={moreLabel}>
                    <Ellipsis className="stroke-[2]" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent>{moreLabel}</TooltipContent>
            </Tooltip>
            {/* From the ⋯ inward: on a phone the ⋯ is at the right, beside the account button. */}
            <PopoverContent
              align={phone ? "end" : "start"}
              sideOffset={12}
              className="w-auto rounded-4xl p-2"
              // Focus stays where it was, on the way in and out. Moved onto the first control, it
              // showed that control's tooltip the moment the popover opened, as a keyboard focus
              // would; moved back to the ⋯, it showed the ⋯'s on the way out.
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => event.preventDefault()}
              // Out of the way of whatever a control in it has just opened; see DISMISS_OVERFLOW_ATTRIBUTE.
              onClick={(event) => {
                if ((event.target as Element).closest(`[${DISMISS_OVERFLOW_ATTRIBUTE}]`)) setMoreOpen(false)
              }}
            >
              <div className="flex flex-col items-start gap-2">
                {hidden.map((group, index) => (
                  <Fragment key={group.id}>
                    {index > 0 && <div aria-hidden className="h-px w-full bg-border" />}
                    <div className="flex flex-row items-center gap-2">{group.content}</div>
                  </Fragment>
                ))}
                {/* The app's own buttons, last: on a phone the header has no room for them. */}
                {phone && (
                  <>
                    {hidden.length > 0 && <div aria-hidden className="h-px w-full bg-border" />}
                    <div className="flex flex-row items-center gap-2">
                      <AppControls isApp />
                    </div>
                  </>
                )}
              </div>
            </PopoverContent>
          </Popover>
          {/* Only between the ⋯ and groups still on the toolbar: with every group folded away, a
              rule here would sit beside the header's own rule before the app's buttons. */}
          {shown.length > 0 && <ToolbarDivider />}
        </>
      )}

      {shown.map((group, index) => (
        <Fragment key={group.id}>
          {index > 0 && <ToolbarDivider />}
          <div
            ref={(node) => {
              if (node) nodes.current.set(group.id, node)
              else nodes.current.delete(group.id)
            }}
            className="flex shrink-0 flex-row items-center gap-2"
          >
            {group.content}
          </div>
        </Fragment>
      ))}
    </div>
  )
}
